import { FC, useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { MAX_WIDTH } from '../../constants/platform';
import MyAccountMenu from '../../components/MyAccountMenu/MyAccountMenu';
import PageTitle from '../../components/PageTitle';
import { COLORS, FONTS } from '../../constants';
import { IActivity } from '../../types/activities.types';
import ActivityItem from './components/ActivityItem';
import { Footer } from '../../components/Footer';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import ActivityApi from '../../services/api/activity.api';
import dayjs from 'dayjs';
import { ActivitiesProps } from './type';
import { Loading } from '../../components';
import { NavigationService } from '../../services/navigation';
import { ActivityTabItem } from '../Home/components/Activities';
import MonthSwitcher from '../../components/MonthSwitcher/MonthSwitcher';
import { IFormData } from '../../components/FormHelper/FormHelper.types';

const Activities: FC<ActivitiesProps> = ({ navigation }) => {
  const isDesktop = useIsDesktop();
  const [activities, setActivities] = useState<IActivity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activityTab, setActivityTab] = useState<string>('today');
  const [monthDate, setMonthDate] = useState<Date>(new Date());

  const getTodayStartEnd = () => {
    let today = new Date();

    return {
      start: `${dayjs(today).format('YYYY-MM-DD')} 00:00:00`,
      end: `${dayjs(today).format('YYYY-MM-DD')} 23:59:59`,
    };
  };

  const getThisWeekStartEnd = () => {
    let now = new Date();
    const dayOfWeek = now.getDay();

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return {
      start: `${dayjs(now).format('YYYY-MM-DD HH:mm:ss')}`,
      end: `${dayjs(endOfWeek).format('YYYY-MM-DD HH:mm:ss')}`,
    };
  };

  const getMonthStartEnd = useCallback(() => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();

    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    return {
      start: `${dayjs(start).format('YYYY-MM-DD')} 00:00:00`,
      end: `${dayjs(end).format('YYYY-MM-DD')} 23:59:59`,
    };
  }, [monthDate]);

  const handleRedirectDetail = (item: IActivity) => {
    NavigationService.push('Root', {
      screen: 'MenuTab',
      params: {
        screen: 'ActivitiesDetail',
        params: {
          id: item.id,
        },
      },
    });
  };

  const fetchActivities = useCallback(
    async (type: 'today' | 'thisWeek' | 'month') => {
      let filterType = type === 'today' ? 'today' : 'this-week';
      let dateFilter = {
        start: '',
        end: '',
      };

      switch (type) {
        case 'today':
          dateFilter = getTodayStartEnd();
          break;
        case 'thisWeek':
          dateFilter = getThisWeekStartEnd();
          break;
        case 'month':
          dateFilter = getMonthStartEnd();
          break;
        default:
      }
      getActivities({ type: filterType });
    },
    [getMonthStartEnd]
  );

  const getActivities = async (filterData: IFormData) => {
    setIsLoading(true);
    ActivityApi.getActivities(filterData)
      .then((res) => {
        setActivities(res.data.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchActivitiesMonth = async (monthDateObj: Date) => {
    const year = monthDateObj.getFullYear();
    const month = monthDateObj.getMonth();
    const now = new Date();

    const start =
      now.getMonth() === month && now.getFullYear() === year
        ? now
        : new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    let dateFilter = {
      start_time: `${dayjs(start).format('YYYY-MM-DD')} 00:00:00`,
      end_time: `${dayjs(end).format('YYYY-MM-DD')} 23:59:59`,
    };
    getActivities(dateFilter);
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchActivities('today');
      setActivityTab('today');
    });

    return unsubscribe;
  }, [fetchActivities, navigation]);

  if (isDesktop()) {
    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <View
          style={{
            maxWidth: MAX_WIDTH,
            width: '90%',
            marginHorizontal: 'auto',
            paddingTop: 40,
            marginBottom: 200,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <View style={{ width: '22%', minWidth: 230, marginRight: 20 }}>
              <MyAccountMenu />
            </View>
            <View style={{ width: '70%' }}>
              <PageTitle title="Upcoming Activities" haveGoBackButton={true} />
              <View
                style={{
                  paddingHorizontal: 40,
                  paddingTop: 40,
                  backgroundColor: COLORS.LIGHT_RED,
                  borderRadius: 18,
                  flexDirection: 'column',
                  shadowColor: '#F0F0F0',
                  shadowOffset: { width: 0, height: 12 },
                  shadowOpacity: 1,
                  shadowRadius: 32,
                  elevation: 20,
                }}
              >
                <View
                  style={[
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      borderRadius: 10,
                      overflow: 'hidden',
                      backgroundColor: '#F5F5F5',
                      marginBottom: 30,
                    },
                    isDesktop()
                      ? {
                          backgroundColor: '#F5F5F5',
                        }
                      : {
                          backgroundColor: COLORS.WHITE,
                        },
                  ]}
                >
                  <ActivityTabItem
                    active={activityTab == 'today'}
                    onPress={() => {
                      setActivityTab('today');
                      fetchActivities('today');
                    }}
                    title="Today"
                  />
                  <ActivityTabItem
                    active={activityTab == 'thisWeek'}
                    onPress={() => {
                      setActivityTab('thisWeek');
                      fetchActivities('thisWeek');
                    }}
                    title="This Week"
                  />
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '44%',
                      height: isDesktop() ? 45 : '100%',
                      backgroundColor:
                        activityTab === ''
                          ? COLORS.RED_PRIMARY
                          : isDesktop()
                          ? COLORS.LIGHT_GREY
                          : COLORS.WHITE,
                      borderRadius: 10,
                    }}
                  >
                    <MonthSwitcher
                      active={activityTab == ''}
                      onChangeDate={(date) => {
                        setActivityTab('');
                        fetchActivitiesMonth(date);
                      }}
                    />
                  </View>
                </View>
                {isLoading && (
                  <View
                    style={{
                      marginBottom: 30,
                    }}
                  >
                    <Loading />
                  </View>
                )}
                {!isLoading && (
                  <View>
                    {activities.length > 0 ? (
                      <View style={{ flexDirection: 'column' }}>
                        {activities.length > 0 &&
                          activities.map((item, index) => {
                            return (
                              <View
                                style={{
                                  marginBottom: 30,
                                }}
                              >
                                <ActivityItem
                                  item={item}
                                  onPress={() => handleRedirectDetail(item)}
                                />
                              </View>
                            );
                          })}
                      </View>
                    ) : (
                      <View style={styles.noDataWrapper}>
                        <Text
                          style={[
                            styles.noDataText,
                            { fontSize: 20, marginBottom: 15 },
                          ]}
                        >
                          THERE ARE CURRENTLY NO ACTIVITIES
                        </Text>
                      </View>
                    )}
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>
        <Footer />
      </ScrollView>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <PageTitle title={'UPCOMMING ACTIVITIES'} haveGoBackButton={true} />
      <View
        style={{
          paddingHorizontal: 20,
          paddingVertical: 20,
          backgroundColor: '#F9F1F1',
        }}
      >
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 10,
              overflow: 'hidden',
              backgroundColor: '#F5F5F5',
              marginBottom: 30,
            },
            isDesktop()
              ? {
                  backgroundColor: '#F5F5F5',
                }
              : {
                  backgroundColor: COLORS.WHITE,
                },
          ]}
        >
          <ActivityTabItem
            active={activityTab == 'today'}
            onPress={() => {
              setActivityTab('today');
              fetchActivities('today');
            }}
            title="Today"
          />
          <ActivityTabItem
            active={activityTab == 'thisWeek'}
            onPress={() => {
              setActivityTab('thisWeek');
              fetchActivities('thisWeek');
            }}
            title="This Week"
          />
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              width: '44%',
              height: isDesktop() ? 45 : '100%',
              backgroundColor:
                activityTab === ''
                  ? COLORS.RED_PRIMARY
                  : isDesktop()
                  ? COLORS.LIGHT_GREY
                  : COLORS.WHITE,
              borderRadius: 10,
            }}
          >
            <MonthSwitcher
              active={activityTab == ''}
              onChangeDate={(date) => {
                setActivityTab('');
                fetchActivitiesMonth(date);
              }}
            />
          </View>
        </View>
        {isLoading && <Loading />}
        {!isLoading && (
          <View>
            {activities.length > 0 ? (
              <View style={{ flexDirection: 'column' }}>
                {activities.length > 0 &&
                  activities.map((item, index) => {
                    return (
                      <View
                        style={{
                          marginBottom: 30,
                        }}
                      >
                        <ActivityItem
                          item={item}
                          onPress={() => handleRedirectDetail(item)}
                        />
                      </View>
                    );
                  })}
              </View>
            ) : (
              <View style={styles.noDataWrapper}>
                <Text
                  style={[
                    styles.noDataText,
                    { fontSize: 16, marginBottom: 15 },
                  ]}
                >
                  THERE ARE CURRENTLY NO ACTIVITIES
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
      <Footer />
    </ScrollView>
  );
};

export default Activities;

const styles = StyleSheet.create({
  noDataWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingBottom: 20,
    width: '100%',
  },
  noDataText: {
    color: COLORS.BLACK,
    textTransform: 'uppercase',
    fontFamily: FONTS.AvenirRegular,
    textAlign: 'center',
  },
});
